import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation, useOutletContext } from "react-router-dom";
import moment from "moment-timezone";
import UserFilms from "../user/UserFilms";
import { useAuth } from "../../contexts/AuthContext";
import {
  FilmDetailsContainer,
  FilmImage,
  ScreeningsList,
  FilmTitle,
  TitleRow,
  ExternalLinkButton,
  ScreeningsContainer,
  LeftColumn,
  RightColumn,
  ContentWrapper,
  CalendarWrapper,
  ShortFilmSubtitle,
  LockButton,
} from "./FilmDetails.styled";
import Loader from "../common/Loader";
import DirectorLink from "../common/DirectorLink";
import CalendarView from "../calendar/CalendarView";
import { ChevronLeft, ChevronRight, OpenInNew, Lock, LockOutlined } from '@mui/icons-material';
import { NavigationArrow } from "./FilmDetails.styled";

function FilmDetails() {
  const { filmId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, token } = useAuth();
  const { hideShortFilms } = useOutletContext() || {};

  const [film, setFilm] = useState(null);
  const [screenings, setScreenings] = useState([]);
  const [priority, setPriority] = useState(0);
  const [films, setFilms] = useState(location.state?.films || []);
  const [loadingFilms, setLoadingFilms] = useState(films.length === 0);
  const [parentFilm, setParentFilm] = useState(null);
  const [lockedScreeningIds, setLockedScreeningIds] = useState(new Set());

  
  const locationParts = location.pathname.split("/");
  const festivalIndex = locationParts.indexOf("festivals");
  const festivalId = festivalIndex !== -1 ? locationParts[festivalIndex + 1] : null;

  
  useEffect(() => {
    async function fetchFilmDetails() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/films/${filmId}`
        );
        const data = await response.json();
        setFilm(data); 
      } catch (error) {
        console.error("Error fetching film details:", error);
      }
    }
    fetchFilmDetails();
  }, [filmId]);

  useEffect(() => {
    if (!film?.parent_film_id) {
      setParentFilm(null);
      return;
    }
    const found = films.find((f) => f.film_id === film.parent_film_id);
    if (found) {
      setParentFilm(found);
    } else {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/films/${film.parent_film_id}`)
        .then((res) => res.json())
        .then((data) => setParentFilm(data))
        .catch(() => setParentFilm(null));
    }
  }, [film, films]);

  useEffect(() => {
    async function fetchFilmScreenings() {
      try {
        const screeningsResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/screenings`
        );
        const allScreenings = await screeningsResponse.json();
        const filmScreenings = allScreenings.filter(
          (screening) => screening.film_id === Number(filmId)
        );

        const venuesResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/venues`
        );
        const venues = await venuesResponse.json();

        const screeningsWithVenues = filmScreenings.map((screening) => {
          const venue = venues.find((v) => v.venue_id === screening.venue_id);
          return {
            ...screening,
            venueName: venue ? venue.name : "Unknown Venue",
          };
        });

        setScreenings(screeningsWithVenues.sort(
          (a, b) => new Date(a.start_time) - new Date(b.start_time)
        ));
      } catch (error) {
        console.error("Error fetching film screenings:", error);
      }
    }
    fetchFilmScreenings();
  }, [filmId]);

  
  useEffect(() => {
    if (films.length === 0 && festivalId) {
      setLoadingFilms(true);
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/films/festival/${festivalId}`)
        .then((res) => res.json())
        .then((data) => {
          setFilms(data);
          setLoadingFilms(false);
        })
        .catch((error) => {
          console.error("Error fetching festival films:", error);
          setLoadingFilms(false);
        });
    }
  }, [festivalId, films.length]);

  
  useEffect(() => {
    async function fetchUserFilmPriority() {
      if (!currentUser || !token) return;
  
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/userfilms/${filmId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
  
        if (response.ok) {
          const data = await response.json();
          setPriority(data.priority || 0);
        } else {
          console.warn("Could not fetch user film priority");
          setPriority(0);
        }
      } catch (error) {
        console.error("Error fetching user film priority:", error);
        setPriority(0);
      }
    }
    fetchUserFilmPriority();
  }, [filmId, currentUser, token]);
  // Fetch locked screenings
  useEffect(() => {
    async function fetchLockedScreenings() {
      if (!currentUser || !token) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/userscreenings`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setLockedScreeningIds(new Set(data.map((us) => us.screening_id)));
        }
      } catch (error) {
        console.error("Error fetching locked screenings:", error);
      }
    }
    fetchLockedScreenings();
  }, [currentUser, token]);

  const handleToggleLock = async (screeningId) => {
    if (!token) return;
    const isLocked = lockedScreeningIds.has(screeningId);

    try {
      if (isLocked) {
        await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/userscreenings/screening/${screeningId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setLockedScreeningIds((prev) => {
          const next = new Set(prev);
          next.delete(screeningId);
          return next;
        });
      } else {
        await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/userscreenings`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ screening_id: screeningId }),
          }
        );
        setLockedScreeningIds((prev) => new Set(prev).add(screeningId));
        // Auto-promote to Definitely on the frontend to match backend behavior
        if (priority < 2) {
          setPriority(2);
        }
      }
    } catch (error) {
      console.error("Error toggling screening lock:", error);
    }
  };

  const navigableFilms = hideShortFilms
    ? films.filter((f) => f.parent_film_id == null && f.screenings && f.screenings.length > 0)
    : films;
  const currentIndex = navigableFilms.findIndex((f) => f.film_id === Number(filmId));
  const prevFilm = currentIndex > 0 ? navigableFilms[currentIndex - 1] : null;
  const nextFilm = currentIndex < navigableFilms.length - 1 ? navigableFilms[currentIndex + 1] : null;

  
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft" && prevFilm) {
        navigate(`/festivals/${festivalId}/films/${prevFilm.film_id}`, { state: { films } });
      } else if (event.key === "ArrowRight" && nextFilm) {
        navigate(`/festivals/${festivalId}/films/${nextFilm.film_id}`, { state: { films } });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [prevFilm, nextFilm, navigate, festivalId, films]);

  const handleNavigation = (direction) => {
    const targetFilm = direction === 'prev' ? prevFilm : nextFilm;
    if (targetFilm) {
      navigate(`/festivals/${festivalId}/films/${targetFilm.film_id}`, { state: { films } });
    }
  };

  
  if (loadingFilms) {
    return <Loader />;
  }
  
  if (!film) {
    return <p>Could Not Find Film Details</p>;
  }

  return (
    <>
      {prevFilm && (
        <NavigationArrow
          className="left"
          onClick={() => handleNavigation("prev")}
          aria-label="Previous film"
        >
          <ChevronLeft fontSize="large" />
        </NavigationArrow>
      )}
      <FilmDetailsContainer>
        <ContentWrapper>
          <LeftColumn>
            <FilmImage src={film.image} alt={film.title} />
            {currentUser && (
              <UserFilms
                filmId={Number(filmId)}
                title={film.title}
                priority={priority}
                onPriorityChange={(filmId, newPriority) =>
                  setPriority(newPriority)
                }
              />
            )}
          </LeftColumn>

          <RightColumn>
            <TitleRow>
              <FilmTitle>{film.title}</FilmTitle>
              {film.external_url && (
                <ExternalLinkButton href={film.external_url} target="_blank" rel="noopener noreferrer">
                  <OpenInNew fontSize="small" />
                  SXSW
                </ExternalLinkButton>
              )}
            </TitleRow>
            {film.parent_film_id && parentFilm && (
              <ShortFilmSubtitle>
                Short Film — Part of{" "}
                <Link to={`/festivals/${festivalId}/films/${film.parent_film_id}`} state={{ films }}>
                  {parentFilm.title}
                </Link>
              </ShortFilmSubtitle>
            )}
            <p>{film.description}</p>
            <p>
              <strong>Director:</strong> <DirectorLink director={film.director} />
            </p>
            {film.duration && (
              <p>
                <strong>Runtime:</strong> {film.duration} min
              </p>
            )}
            <p>
              <strong>Genre:</strong>{" "}
              {film.genres && film.genres.length > 0
                ? film.genres.map((genre) => genre.genre_name).join(", ")
                : "Unknown"}
            </p>
          </RightColumn>
        </ContentWrapper>

        <ScreeningsContainer>
          <h2>
            {screenings.length} Screening{screenings.length !== 1 ? "s" : ""}{" "}
            for {film.title}
          </h2>
          <ScreeningsList>
            {screenings.map((screening) => (
              <li key={screening.screening_id}>
                <span>
                  {moment(screening.start_time)
                    .tz(screening.timezone)
                    .format("M/D/YYYY, h:mm A")}
                  {" @ "}
                  {screening.venueName}
                </span>
                {currentUser && (
                  <LockButton
                    $isLocked={lockedScreeningIds.has(screening.screening_id)}
                    onClick={() => handleToggleLock(screening.screening_id)}
                    title={lockedScreeningIds.has(screening.screening_id) ? "Unlock screening" : "Lock screening into schedule"}
                  >
                    {lockedScreeningIds.has(screening.screening_id) ? (
                      <Lock />
                    ) : (
                      <LockOutlined />
                    )}
                  </LockButton>
                )}
              </li>
            ))}
          </ScreeningsList>
        </ScreeningsContainer>
        {/* <CalendarWrapper> */}
          <CalendarView
            mode="per-day"
            filmId={filmId}
            externalLockedScreeningIds={lockedScreeningIds}
            onExternalToggleLock={currentUser ? handleToggleLock : null}
          />
        {/* </CalendarWrapper> */}
      </FilmDetailsContainer>
      {nextFilm && (
        <NavigationArrow
          className="right"
          onClick={() => handleNavigation("next")}
          aria-label="Next film"
        >
          <ChevronRight fontSize="large" />
        </NavigationArrow>
      )}
    </>
  );
}

export default FilmDetails;
